import { useEffect, useState } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { LoginPage } from './pages/LoginPage.js';
import { HomePage } from './pages/HomePage.js';
import { ModelsListPage } from './pages/ModelsListPage.js';
import { ModelPage } from './pages/ModelPage.js';
import { TopPostsPage } from './pages/TopPostsPage.js';
import { TopDayPage } from './pages/TopDayPage.js';
import { TopByTagsPage } from './pages/TopByTagsPage.js';
import { AdminPage } from './pages/AdminPage.js';
import { AnalyzedModelsPage } from './pages/AnalyzedModelsPage.js';
import { Sidebar } from './ui/Sidebar.js';
import { ThemeToggle } from './ui/ThemeToggle.js';
import { api, getToken, setToken } from './api/client.js';
import type { User } from '@wml-crm/shared';

const PAGE_TITLES: Array<[RegExp, string]> = [
  [/^\/$/, 'Поиск'],
  [/^\/models$/, 'Все модели'],
  [/^\/top-posts$/, 'Топ 200 постов'],
  [/^\/top-day$/, 'Топ 50 за день'],
  [/^\/top-tags$/, 'Топ 100 по типажам'],
  [/^\/analyzed$/, 'Проанализированные модели'],
  [/^\/model\//, 'Модель'],
  [/^\/admin/, 'Админка'],
];

export function App() {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);
  const nav = useNavigate();
  const location = useLocation();
  const pageTitle = PAGE_TITLES.find(([re]) => re.test(location.pathname))?.[1] ?? '';

  async function loadMe() {
    const token = getToken();
    if (!token) { setUser(null); setReady(true); return; }
    try {
      const { user } = await api<{ user: User }>('/api/auth/me');
      setUser(user);
    } catch {
      setToken(null);
      setUser(null);
    } finally {
      setReady(true);
    }
  }

  useEffect(() => { loadMe(); }, []);

  if (!ready) return null;
  if (!user) return <LoginPage onLogin={loadMe} />;

  return (
    <div className="app-shell">
      <Sidebar user={user} />
      <div className="main-area">
        <div className="page-topbar">
          <div className="page-title">{pageTitle}</div>
          <div className="spacer" />
          <ThemeToggle />
          <button
            className="icon-btn"
            title="Выйти"
            onClick={() => { setToken(null); setUser(null); nav('/'); }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <path d="m16 17 5-5-5-5" />
              <path d="M21 12H9" />
            </svg>
          </button>
        </div>

        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/models" element={<ModelsListPage />} />
          <Route path="/top-posts" element={<TopPostsPage />} />
          <Route path="/top-day" element={<TopDayPage />} />
          <Route path="/top-tags" element={<TopByTagsPage />} />
          {user.canViewAnalyzed && (
            <Route path="/analyzed" element={<AnalyzedModelsPage me={user} />} />
          )}
          <Route path="/model/:id" element={<ModelPage />} />
          <Route path="/admin" element={<AdminPage me={user} />} />
          <Route path="*" element={<HomePage />} />
        </Routes>
      </div>
    </div>
  );
}
